/**
 * Accessible modal dialog (WCAG 2.1 AA): role="dialog" aria-modal, focus trapped inside, Esc
 * closes, focus restored to the opener on close. Reused by the image lightbox + admin invite
 * dialog. (The widget launcher has its own framework-free trap — this is the React one.)
 */
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { CloseIcon } from "./icons.js";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function Dialog({
  open,
  onClose,
  label,
  children,
  className,
  role = "dialog",
  describedBy,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
  className?: string;
  /** "alertdialog" for irreversible/high-risk confirmations (WAI-ARIA alertdialog pattern) —
   *  callers must NOT also render their own nested role="alertdialog"; pass it here instead. */
  role?: "dialog" | "alertdialog";
  /** Optional aria-describedby, e.g. an alertdialog's body copy id. */
  describedBy?: string;
}): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => {
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div className="td-overlay" onMouseDown={onClose}>
      <div
        className={`td-dialog ${className ?? ""}`}
        role={role}
        aria-modal="true"
        aria-label={label}
        aria-describedby={describedBy}
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button type="button" className="td-dialog__close" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
        {children}
      </div>
    </div>
  );
}
